import { useEffect, useMemo, useState } from "react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import AttendanceWidget from "@/components/schedule/AttendanceWidget";
import DayView from "@/components/schedule/DayView";
import WeekView from "@/components/schedule/WeekView";
import MonthView from "@/components/schedule/MonthView";
import AttendanceHistoryTable from "@/components/schedule/AttendanceHistoryTable";
import ScheduleEventFormDialog from "@/components/dialogs/ScheduleEventFormDialog";
import api from "@/lib/api";
import { format, addDays, subDays, addMonths, subMonths, startOfWeek, startOfMonth } from "date-fns";
import { ChevronLeft, ChevronRight } from "lucide-react";

export default function MySchedule() {
  const [view, setView] = useState("day");
  const [date, setDate] = useState(format(new Date(), "yyyy-MM-dd"));
  const [dayData, setDayData] = useState({ events: [], available_slots: [] });
  const [rangeEvents, setRangeEvents] = useState([]);
  const [attendance, setAttendance] = useState(null);
  const [history, setHistory] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);

  const loadAttendance = () => api.get("/attendance/today").then((r) => setAttendance(r.data)).catch(() => {});
  const loadHistory = () => api.get("/attendance/history").then((r) => setHistory(r.data)).catch(() => {});

  const loadSchedule = () => {
    api.get("/schedule", { params: { date, view } }).then((r) => {
      if (view === "day") setDayData(r.data);
      else setRangeEvents(r.data.events);
    }).catch(() => {});
  };

  useEffect(() => { loadAttendance(); loadHistory(); }, []);
  useEffect(() => { loadSchedule(); }, [date, view]);

  const shift = (dir) => {
    const d = new Date(date);
    if (view === "day") setDate(format(dir > 0 ? addDays(d, 1) : subDays(d, 1), "yyyy-MM-dd"));
    else if (view === "week") setDate(format(dir > 0 ? addDays(d, 7) : subDays(d, 7), "yyyy-MM-dd"));
    else setDate(format(dir > 0 ? addMonths(d, 1) : subMonths(d, 1), "yyyy-MM-dd"));
  };

  const weekDays = useMemo(() => {
    const start = startOfWeek(new Date(date), { weekStartsOn: 1 });
    return Array.from({ length: 7 }).map((_, i) => {
      const d = addDays(start, i);
      const dStr = format(d, "yyyy-MM-dd");
      return { date: dStr, label: format(d, "EEE"), dayNum: format(d, "d MMM"), events: rangeEvents.filter((e) => e.date === dStr) };
    });
  }, [date, rangeEvents]);

  const monthWeeks = useMemo(() => {
    const start = startOfWeek(startOfMonth(new Date(date)), { weekStartsOn: 1 });
    const monthNum = new Date(date).getMonth();
    const cells = Array.from({ length: 42 }).map((_, i) => {
      const d = addDays(start, i);
      const dStr = format(d, "yyyy-MM-dd");
      return { date: dStr, dayNum: format(d, "d"), inMonth: d.getMonth() === monthNum, count: rangeEvents.filter((e) => e.date === dStr).length };
    });
    const weeks = [];
    for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7));
    return weeks;
  }, [date, rangeEvents]);

  const openAdd = () => { setEditingEvent(null); setDialogOpen(true); };
  const openEdit = (ev) => { setEditingEvent(ev); setDialogOpen(true); };
  const refreshAll = () => { loadSchedule(); loadAttendance(); loadHistory(); };

  return (
    <DashboardLayout title="My Schedule & Attendance">
      <AttendanceWidget record={attendance} onChange={setAttendance} />

      <div className="flex flex-wrap items-center justify-between gap-3 mt-6 mb-4">
        <Tabs value={view} onValueChange={setView}>
          <TabsList data-testid="schedule-view-tabs">
            <TabsTrigger value="day" data-testid="schedule-tab-day">Daily</TabsTrigger>
            <TabsTrigger value="week" data-testid="schedule-tab-week">Weekly</TabsTrigger>
            <TabsTrigger value="month" data-testid="schedule-tab-month">Monthly</TabsTrigger>
          </TabsList>
        </Tabs>
        <div className="flex items-center gap-2">
          <Button size="icon" variant="outline" onClick={() => shift(-1)} data-testid="schedule-prev-button"><ChevronLeft className="h-4 w-4" /></Button>
          <span className="text-sm font-medium text-[#1C1C1E] w-32 text-center" data-testid="schedule-current-date">{date}</span>
          <Button size="icon" variant="outline" onClick={() => shift(1)} data-testid="schedule-next-button"><ChevronRight className="h-4 w-4" /></Button>
        </div>
      </div>

      {view === "day" && (
        <DayView date={date} events={dayData.events} availableSlots={dayData.available_slots} onAdd={openAdd} onEdit={openEdit} onRefresh={refreshAll} />
      )}
      {view === "week" && <WeekView days={weekDays} onSelectDate={(d) => { setDate(d); setView("day"); }} />}
      {view === "month" && <MonthView weeks={monthWeeks} onSelectDate={(d) => { setDate(d); setView("day"); }} />}

      <div className="mt-8">
        <h3 className="font-heading text-sm font-semibold text-[#1C1C1E] mb-3">Attendance History</h3>
        <AttendanceHistoryTable records={history} />
      </div>

      <ScheduleEventFormDialog open={dialogOpen} onOpenChange={setDialogOpen} defaultDate={date} editingEvent={editingEvent} onSaved={refreshAll} />
    </DashboardLayout>
  );
}
